import { Rest } from '../../../../util/rest';

const BASE_PATH = '/rest/ai/leads-report';
const PUBLIC_BASE_PATH = `/${Rest.contextPath}${BASE_PATH}`;
const SESSION_RENEWAL_WINDOW_MS = 60000;

let activeSession = {
  uuid: null,
  expiresAtMs: 0,
  request: null,
};

const parseResponse = async (response) => {
  if (!response.ok) {
    const error = new Error('Leads report request failed');
    error.status = response.status;
    try {
      error.payload = await response.json();
    } catch (_ignored) {
      error.payload = null;
    }
    throw error;
  }

  return response.json();
};

export function loadLeadsReportSnapshot(uuid, { signal } = {}) {
  return loadLeadsReportResource(uuid, undefined, signal);
}

export function loadLeadsReportSection(uuid, section, { signal } = {}) {
  return loadLeadsReportResource(uuid, section, signal);
}

export function ensureLeadsReportSession(uuid, { force = false, signal } = {}) {
  const now = Date.now();
  if (!force
      && activeSession.uuid === uuid
      && activeSession.expiresAtMs - now > SESSION_RENEWAL_WINDOW_MS) {
    return Promise.resolve({ expires_at: new Date(activeSession.expiresAtMs).toISOString() });
  }

  if (activeSession.request && activeSession.uuid === uuid) {
    return activeSession.request;
  }

  activeSession.uuid = uuid;
  activeSession.request = fetch(`${PUBLIC_BASE_PATH}/${encodeURIComponent(uuid)}/session`, {
    method: 'POST',
    credentials: 'same-origin',
    signal,
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(parseResponse)
    .then((session) => {
      const expiresAtMs = Date.parse(session.expires_at);
      activeSession.expiresAtMs = Number.isFinite(expiresAtMs)
        ? expiresAtMs
        : Date.now() + (Number(session.expires_in_seconds || 300) * 1000);
      return session;
    })
    .finally(() => {
      activeSession.request = null;
    });

  return activeSession.request;
}

export function resetLeadsReportSession() {
  activeSession = { uuid: null, expiresAtMs: 0, request: null };
}

function loadLeadsReportResource(uuid, section, signal) {
  const suffix = section ? `/sections/${encodeURIComponent(section)}` : '';
  const request = () => fetch(`${PUBLIC_BASE_PATH}/${encodeURIComponent(uuid)}${suffix}`, {
    credentials: 'same-origin',
    signal,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return ensureLeadsReportSession(uuid, { signal })
    .then(request)
    .then(async (response) => {
      if (response.status !== 401) {
        return parseResponse(response);
      }

      await ensureLeadsReportSession(uuid, { force: true, signal });
      return request().then(parseResponse);
    });
}
